import { ref, watch, getCurrentInstance } from 'vue';

import DashboardService from '@/services/DashboardService';

import { ITEM_STATUS } from '@/components/Dashboard/common/WidgetSettingPanel/options/styles';
import { sortLongString } from '@/utils/dashboard';

const isCustomMetricKey = (key) => /^custom-metric:\d+/.test(key);
const isCustomBreakdownKey = (key) => !isNaN(key);

/**
 * Resolves field labels for the current list using the availableFields/v2 API.
 *
 * availableFields/v2 is refetched when any of these change (order-insensitive):
 *   - assetIds
 *   - breakdowns
 *   - widgetType
 *
 * Custom metric/breakdown status and name are resolved synchronously from the
 * Vuex store (customMetricsMap / dimensionById). These react to store changes
 * automatically via Vue's render-time reactivity — no explicit watch needed.
 *
 * Metric alias changes are also handled via render-time reactivity
 * (aliasesByIndex getter).
 *
 * Deduplication: DashboardService.getAvailableFieldsV2 is memoized by payload,
 * so identical calls share one Promise with no duplicate HTTP request.
 *
 * @param {'metric'|'breakdown'} fieldType
 */
export function useFieldLabelsV2(fieldType) {
    const instance = getCurrentInstance();

    // All fields from the last successful API response, keyed by field name.
    // Storing the full response (not filtered by currentList) means new keys
    // added to currentList can be looked up immediately without re-fetching.
    const apiFieldMap = ref({});
    const fieldLabelsLoading = ref(false); // true while getAvailableFieldsV2 is in-flight

    const isCustomKey =
        fieldType === 'metric' ? isCustomMetricKey : isCustomBreakdownKey;

    watch(
        () => {
            const vm = instance.proxy;
            // Serialize API params to a stable string for order-insensitive comparison.
            // Vue compares watch source return values with ===, so returning a primitive
            // string avoids false positives from new array/object references.
            return JSON.stringify({
                assetIds: sortLongString(
                    vm.selectedSource?.map((a) => a.assetId) ?? []
                ),
                breakdowns: sortLongString(vm.currentBreakdownIds ?? []),
                widgetType: vm.isAnalytic ? 'normal' : 'marketing',
                language: vm.$i18n?.locale ?? 'en',
                isForDatastream: vm.isForDatastream ?? false,
            });
        },
        async (paramsKey) => {
            const {
                assetIds,
                breakdowns,
                widgetType,
                language,
                isForDatastream,
            } = JSON.parse(paramsKey);

            if (!assetIds.length) return;

            fieldLabelsLoading.value = true;
            try {
                const result = await DashboardService.getAvailableFieldsV2({
                    assetIds,
                    language,
                    widgetType,
                    breakdowns,
                    isForDatastream,
                });

                const list =
                    fieldType === 'metric'
                        ? result?.fields ?? []
                        : result?.breakdowns ?? [];

                apiFieldMap.value = list.reduce((acc, field) => {
                    if (field.name) acc[field.name] = field;
                    return acc;
                }, {});
            } catch (_) {
                // silently fall back — component will use FE locale
            } finally {
                fieldLabelsLoading.value = false;
            }
        },
        { immediate: true }
    );

    // Resolves custom key status directly from the Vuex store.
    // Called during component render → Vue's render-time reactivity tracks
    // customMetricsMap / dimensionById and re-renders on changes automatically.
    function getCustomKeyStatus(key) {
        const vm = instance.proxy;
        if (fieldType === 'metric') {
            return vm.$store.getters['newDashboard/customMetricsMap'][key]
                ? ITEM_STATUS.NORMAL
                : ITEM_STATUS.INVALID;
        }
        return vm.$store.getters['newDashboard/dimensionById'][key]
            ? ITEM_STATUS.NORMAL
            : ITEM_STATUS.INVALID;
    }

    // Resolves custom key display name directly from the Vuex store.
    // Same render-time reactivity applies.
    function getCustomKeyName(key) {
        const vm = instance.proxy;
        if (fieldType === 'metric') {
            return (
                vm.$store.getters['newDashboard/customMetricsMap'][key]?.name ??
                null
            );
        }
        return (
            vm.$store.getters['newDashboard/dimensionById'][key]?.name ?? null
        );
    }

    function getFieldStatus(item) {
        const key = item.key;
        if (!key) return ITEM_STATUS.INVALID;

        if (isCustomKey(key)) return getCustomKeyStatus(key);

        const isNewField = !(key in apiFieldMap.value);
        if (fieldLabelsLoading.value && isNewField) return ITEM_STATUS.LOADING;
        return apiFieldMap.value[key]
            ? ITEM_STATUS.NORMAL
            : ITEM_STATUS.INVALID;
    }

    function getFieldName(item) {
        const key = item.key;
        if (!key) return null;

        if (isCustomKey(key)) return getCustomKeyName(key);

        const isNewField = !(key in apiFieldMap.value);
        if (fieldLabelsLoading.value && isNewField) return '';

        const field = apiFieldMap.value[key];
        if (!field) return null;

        if (fieldType === 'metric') {
            const vm = instance.proxy;
            // Alias takes priority over BE locale.
            // aliasesByIndex is accessed during render → reactive automatically.
            const alias =
                vm.$store.getters['newDashboard/aliasesByIndex'][key]
                    ?.displayFieldName;
            return alias || field.locale || null;
        }

        return field.locale || null;
    }

    return { getFieldStatus, getFieldName, fieldLabelsLoading };
}
